import { Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { UserRole } from '../../shared/models/user-role.enum';
import { HttpService } from '../../shared/services/http.service';
import { UserProfileService } from '../../shared/services/user-profile.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private static readonly TOKEN_KEY = 'auth_token';
  private static readonly USER_ROLE_KEY = 'userRole';
  private static readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private static readonly EXPIRES_AT_KEY = 'expires_at';
  private static readonly USER_DETAILS_KEY = 'userDetails';
  private static readonly LOGOUT_FLAG_KEY = 'app_logout_flag';
  private static readonly REDIRECT_EVENT_ID_KEY = 'redirectEventId';
  private static readonly REDIRECT_SEGMENT_KEY = 'redirectSegment';
  private static readonly STUDENT_EVENT_KEY = 'studentEvent';
  private static readonly STUDENT_FIRST_LOGIN_KEY = 'studentIsFirstLogin';
  private static readonly TEACHER_FIRST_LOGIN_KEY = 'teacherIsFirstLogin';
  private isLoggingOut = false;

  constructor(
    private router: Router,
    private httpService: HttpService,
    private injector: Injector,
    private snackBar: MatSnackBar,
  ) {
    this.cleanupStaleLogoutFlags();
    window.addEventListener('storage', (event) => {
      if (event.key === AuthService.LOGOUT_FLAG_KEY && event.newValue) {
        this.handleLogoutFromOtherTab();
      }
    });
  }

  private cleanupStaleLogoutFlags(): void {
    const logoutFlag = localStorage.getItem(AuthService.LOGOUT_FLAG_KEY);
    if (logoutFlag) {
      const flagTimestamp = parseInt(logoutFlag, 10);
      if (Date.now() - flagTimestamp > 60000) {
        localStorage.removeItem(AuthService.LOGOUT_FLAG_KEY);
        this.isLoggingOut = false;
      }
    }
  }

  // ---------- TOKEN HANDLING ----------
  // Tokens stored in localStorage so they are shared across tabs (e.g. email links opening new tabs)
  setToken(token: string): void {
    localStorage.setItem(AuthService.TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(AuthService.TOKEN_KEY);
  }

  clearToken(): void {
    localStorage.removeItem(AuthService.TOKEN_KEY);
    localStorage.removeItem(AuthService.REFRESH_TOKEN_KEY);
    localStorage.removeItem(AuthService.EXPIRES_AT_KEY);
  }

  setSession(token: string, refreshToken: string, expiresAt: string): void {
    this.setToken(token);
    localStorage.setItem(AuthService.REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(AuthService.EXPIRES_AT_KEY, expiresAt);
  }

  // ---------- ROLE HANDLING ----------
  setUserRole(role: UserRole): void {
    localStorage.setItem(AuthService.USER_ROLE_KEY, role);
  }

  getUserRole(): UserRole | null {
    const stored = localStorage.getItem(AuthService.USER_ROLE_KEY);
    return stored && Object.values(UserRole).includes(stored as UserRole)
      ? (stored as UserRole)
      : null;
  }

  getCurrentUserRole(): UserRole | null {
    return this.getUserRole();
  }

  storeEventRedirectFromUrl(): void {
    try {
      const urlObj = new URL(window.location.href);
      const segment = urlObj.searchParams.get('segment');

      const pathMatch = urlObj.pathname.match(/\/view\/([a-zA-Z0-9-]+)/);
      const eventId = pathMatch?.[1];

      if (eventId) {
        localStorage.setItem(AuthService.REDIRECT_EVENT_ID_KEY, eventId);
        if (segment) {
          localStorage.setItem(AuthService.REDIRECT_SEGMENT_KEY, segment);
        }
      }
    } catch (e) {
      console.error('Error parsing redirect URL:', e);
    }
  }

  getRedirectEventId(): string | null {
    return localStorage.getItem(AuthService.REDIRECT_EVENT_ID_KEY);
  }

  getRedirectSegment(): string | null {
    return localStorage.getItem(AuthService.REDIRECT_SEGMENT_KEY);
  }

  clearRedirectData(): void {
    localStorage.removeItem(AuthService.REDIRECT_EVENT_ID_KEY);
    localStorage.removeItem(AuthService.REDIRECT_SEGMENT_KEY);
  }

  public getDeviceId(): string {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = 'device_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  getUserEmail(): string | null {
    const raw = localStorage.getItem(AuthService.USER_DETAILS_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw)?.email ?? null; } catch { return null; }
  }

  async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    const email = this.getUserEmail();
    if (!refreshToken || !email) return false;

    const apiUrl = this.resolveApiUrl();
    try {
      const res = await fetch(`${apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, refreshToken }),
      });
      if (!res.ok) return false;
      const result = await res.json();
      const d = result?.data;
      if (!d?.accessToken) return false;
      this.setSession(d.accessToken, d.refreshToken, d.expiresAt);
      return true;
    } catch {
      return false;
    }
  }

  private resolveApiUrl(): string {
    const h = window.location.hostname;
    if (h === '192.168.29.15' || h === '192.168.29.12') return 'https://web-trex-dev-fe.azurewebsites.net/api';
    if (h === 'localhost' || h === 'web-trex-dev-fe.azurewebsites.net') return 'https://web-trex-dev-api.azurewebsites.net/api';
    if (h === 'web-trex-prd-fe-staging.azurewebsites.net') return 'https://web-trex-prd-api-staging.azurewebsites.net/api';
    if (h === 'trex.constructionpathways.com.au') return 'https://web-trex-prd-api.azurewebsites.net/api';
    return 'https://web-trex-dev-api.azurewebsites.net/api';
  }

  // ---------- AUTH VALIDATION ----------
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return !this.isTokenExpired();
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() > exp;
    } catch (e) {
      console.error('Token parsing error:', e);
      return true;
    }
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(AuthService.REFRESH_TOKEN_KEY);
  }

  getAccessTokenExpiry(): Date | null {
    const stored = localStorage.getItem(AuthService.EXPIRES_AT_KEY);
    return stored ? new Date(stored) : null;
  }

  logout(): void {
    this.setLogoutFlag();
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    const token = this.getToken();
    const userDetails = localStorage.getItem(AuthService.USER_DETAILS_KEY);
    let email = '';

    if (userDetails) {
      try {
        const user = JSON.parse(userDetails);
        email = user.email || '';
      } catch (e) {
        console.error('Error parsing user details:', e);
      }
    }

    if (email && token) {
      const payload = {
        allDevices: true,
        email: email,
      };

      this.httpService.post('auth/logout', payload).subscribe({
        next: (response: any) => {
          this.showSuccessToast('User logged out successfully');
          this.clearLocalStorageAndNavigate();
        },
        error: (error) => {
          console.error('Logout API call failed:', error);
          this.showSuccessToast('User logged out successfully');
          this.clearLocalStorageAndNavigate();
        },
      });
    } else {
      this.showSuccessToast('User logged out successfully');
      this.clearLocalStorageAndNavigate();
    }
  }

  private setLogoutFlag(): void {
    localStorage.setItem(AuthService.LOGOUT_FLAG_KEY, Date.now().toString());
    setTimeout(() => {
      localStorage.removeItem(AuthService.LOGOUT_FLAG_KEY);
    }, 1000);
  }

  private handleLogoutFromOtherTab(): void {
    if (!this.isLoggingOut) {
      this.isLoggingOut = true;
      this.showSuccessToast('User logged out successfully');
      this.clearLocalStorage();
      if (!this.router.url.includes('/auth/login')) {
        this.router.navigate(['/auth/login']);
      }

      this.isLoggingOut = false;
    }
  }

  private clearLocalStorageAndNavigate(): void {
    this.clearLocalStorage();
    localStorage.removeItem(AuthService.LOGOUT_FLAG_KEY);
    if (!this.router.url.includes('/auth/login')) {
      this.router.navigate(['/auth/login']);
    }

    this.isLoggingOut = false;
  }

  private clearLocalStorage(): void {
    const dialog = this.injector.get(MatDialog);
    dialog.closeAll();
    this.injector.get(UserProfileService).clear();

    localStorage.removeItem(AuthService.TOKEN_KEY);
    localStorage.removeItem(AuthService.REFRESH_TOKEN_KEY);
    localStorage.removeItem(AuthService.EXPIRES_AT_KEY);
    localStorage.removeItem(AuthService.USER_ROLE_KEY);
    localStorage.removeItem(AuthService.USER_DETAILS_KEY);
    localStorage.removeItem(AuthService.STUDENT_EVENT_KEY);
    localStorage.removeItem(AuthService.STUDENT_FIRST_LOGIN_KEY);
    localStorage.removeItem(AuthService.TEACHER_FIRST_LOGIN_KEY);
    // Do NOT clear REDIRECT_EVENT_ID_KEY here — needed after login
  }

  navigateToLogin(): void {
    if (!this.router.url.includes('/auth/login')) {
      this.router.navigate(['/auth/login']);
    }
  }

  // ---------- REDIRECT BY ROLE ----------
  // redirectByRole(role: UserRole): void {
  //     console.log(role,"redirectByRole");
  //     const routes: Record<UserRole, string> = {
  //         [UserRole.SuperAdmin]: '/dashboard/super-admin',
  //         [UserRole.CsqAdmin]: '/dashboard/csq-admin',
  //         [UserRole.CsqUser]: '/dashboard/csq-user',
  //         [UserRole.Teacher]: '/dashboard/teacher',
  //         [UserRole.Student]: '/dashboard/user',
  //     };
  //     console.log("redirect by role",{routerole:routes[role],role});
  //     this.router.navigate([routes[role] || '/auth/login']);
  // }

  redirectByRole(role: UserRole): Promise<boolean> {
    const routes: Record<UserRole, string> = {
      [UserRole.SuperAdmin]: '/dashboard/super-admin',
      [UserRole.CsqAdmin]: '/dashboard/csq-admin',
      [UserRole.CsqUser]: '/dashboard/csq-user',
      [UserRole.Teacher]: '/dashboard/teacher',
      [UserRole.Student]: '/dashboard/user',
    };

    const targetRoute = routes[role] || '/auth/login';

    return this.router.navigate([targetRoute]);
  }

  private showErrorToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }
}
